import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';

interface Party {
  id: number;
  title: string;
  date: string;
  location: string | null;
  description: string | null;
  imageUrl: string | null;
  createdAt: string;
}

export default function PartiesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    location: '',
    description: '',
    imageUrl: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchParties();
  }, [user, navigate]);

  const fetchParties = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<Party[]>('/api/parties');
      setParties(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load parties');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateParty = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/parties', {
        title: formData.title,
        date: new Date(formData.date).toISOString(),
        location: formData.location || null,
        description: formData.description || null,
        imageUrl: formData.imageUrl || null,
      });
      setFormData({ title: '', date: '', location: '', description: '', imageUrl: '' });
      setShowForm(false);
      fetchParties();
    } catch (err) {
      setError('Failed to create party');
      console.error(err);
    }
  };

  if (loading) return <div className="page-loading">Loading parties...</div>;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const futureParties = parties.filter(
    (p) => new Date(p.date) >= today
  );
  const pastParties = parties.filter(
    (p) => new Date(p.date) < today
  );

  const handlePartyClick = (partyId: number) => {
    navigate(`/parties/${partyId}`);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🎉 Fester</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Create Party Form */}
      <div className="form-card card">
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
          style={{ marginBottom: showForm ? '1rem' : '0' }}
        >
          {showForm ? 'Annuller' : '➕ Opret fest'}
        </button>

        {showForm && (
          <form onSubmit={handleCreateParty} className="auth-form" style={{ marginTop: '1rem' }}>
            <div className="form-group">
              <label>Titel</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Dato & Tid</label>
              <input
                type="datetime-local"
                className="form-input"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Lokation</label>
              <input
                type="text"
                className="form-input"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Beskrivelse</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Billede URL</label>
              <input
                type="url"
                className="form-input"
                value={formData.imageUrl}
                onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              />
            </div>

            <button type="submit" className="btn btn-primary btn-full">
              Opret fest
            </button>
          </form>
        )}
      </div>

      {/* Future Parties */}
      {futureParties.length > 0 && (
        <div className="parties-section">
          <h2>Kommende fester</h2>
          <div className="parties-grid">
            {futureParties.map((party) => (
              <div
                key={party.id}
                className="party-card card"
                onClick={() => handlePartyClick(party.id)}
              >
                {party.imageUrl && (
                  <img src={party.imageUrl} alt={party.title} className="party-image" />
                )}
                <div className="party-body">
                  <h3 className="party-title">{party.title}</h3>
                  <p className="party-date">
                    📅 {new Date(party.date).toLocaleDateString('da-DK', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  {party.location && <p className="party-location">📍 {party.location}</p>}
                  {party.description && <p className="party-desc">{party.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past Parties */}
      {pastParties.length > 0 && (
        <div className="parties-section">
          <h2>Tidligere fester</h2>
          <div className="parties-grid">
            {pastParties.map((party) => (
              <div
                key={party.id}
                className="party-card card party-card-past"
                onClick={() => handlePartyClick(party.id)}
              >
                {party.imageUrl && (
                  <img src={party.imageUrl} alt={party.title} className="party-image" />
                )}
                <div className="party-body">
                  <h3 className="party-title">{party.title}</h3>
                  <p className="party-date">
                    📅 {new Date(party.date).toLocaleDateString('da-DK', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                  {party.location && <p className="party-location">📍 {party.location}</p>}
                  {party.description && <p className="party-desc">{party.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {futureParties.length === 0 && pastParties.length === 0 && (
        <div className="empty-state">
          <span>🎉</span>
          <p>Ingen fester endnu. Opret en!</p>
        </div>
      )}
    </div>
  );
}
